import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ModalShell } from "@/components/ui/ModalShell";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { useAccounts } from "@/hooks/useAccounts";
import { useTransactions, useTransactionTypes } from "@/hooks/useTransactions";
import { formatDate, formatSignedCurrency } from "@/utils/formatters";

interface CategoryTransactionsModalProps {
  categoryId: number | null;
  categoryName: string;
  periodBalance: number;
  start: string;
  end: string;
  onClose: () => void;
}

export const CategoryTransactionsModal = ({
  categoryId,
  categoryName,
  periodBalance,
  start,
  end,
  onClose,
}: CategoryTransactionsModalProps) => {
  const { t } = useTranslation(["common", "categories"]);
  const [viewMode, setViewMode] = useState<"transactions" | "accounts">("transactions");
  const { data: accounts = [] } = useAccounts();
  const { data: transactionTypes = [] } = useTransactionTypes();
  const transactionTypeById = useMemo(
    () => new Map(transactionTypes.map((type) => [type.id, type.name.toUpperCase()])),
    [transactionTypes],
  );
  const accountNameById = useMemo(
    () => new Map(accounts.map((account) => [account.id, account.name])),
    [accounts],
  );
  const { data: transactions = [], isLoading, isError } = useTransactions(
    categoryId === null
      ? undefined
      : {
          category_id_eq: categoryId,
          billable_date_gte: start,
          billable_date_lte: end,
          transaction_date: "desc",
        },
  );
  const accountTotals = useMemo(() => {
    const totals = new Map<string, { total: number; count: number }>();

    transactions.forEach((transaction) => {
      const name =
        accountNameById.get(transaction.account_id) ??
        transaction.account ??
        t("categories.unknownAccount", { defaultValue: "Cuenta sin nombre" });
      const current = totals.get(name) ?? { total: 0, count: 0 };
      const amount = Math.abs(Number(transaction.amount ?? 0));
      const transactionType = transactionTypeById.get(transaction.type_id);
      const signedAmount = transactionType === "EXPENSE"
        ? -amount
        : transactionType === "DEPOSIT"
          ? amount
          : Number(transaction.amount ?? 0);
      totals.set(name, { total: current.total + signedAmount, count: current.count + 1 });
    });

    return [...totals.entries()]
      .map(([name, total]) => ({ name, ...total }))
      .sort((a, b) => b.total - a.total);
  }, [accountNameById, t, transactionTypeById, transactions]);

  if (categoryId === null) return null;

  const subtitle = [
    `${formatDate(start)} — ${formatDate(end)}`,
    `${t("categories.periodBalance", { defaultValue: "Balance del periodo" })}: ${formatSignedCurrency(periodBalance)}`,
  ].join(" · ");

  return (
    <ModalShell
      isOpen={categoryId !== null}
      onClose={onClose}
      title={categoryName}
      subtitle={subtitle}
      labelledBy="category-transactions-title"
      mobileSheet
      maxWidthClassName="max-w-2xl"
      panelClassName="max-h-[min(calc(100dvh-1rem),760px)]"
      contentClassName="px-5 py-2 sm:px-7"
      headerMeta={(
        <SegmentedControl
          value={viewMode}
          onChange={setViewMode}
          options={[
            { id: "transactions", label: t("categories.transactionsList", { defaultValue: "Movimientos" }) },
            { id: "accounts", label: t("categories.transactionsByAccount", { defaultValue: "Por cuenta" }) },
          ]}
        />
      )}
    >
      {isLoading ? <p className="py-10 text-center text-sm text-[var(--app-text-secondary)]">{t("common.status.loading", { defaultValue: "Cargando…" })}</p> : null}
      {isError ? <p className="py-10 text-center text-sm text-[var(--app-danger)]">{t("categories.transactionsLoadError", { defaultValue: "No se pudieron cargar movimientos." })}</p> : null}
      {!isLoading && !isError && transactions.length === 0 ? (
        <p className="py-10 text-center text-sm text-[var(--app-text-secondary)]">
          {t("categories.noTransactionsInPeriod", { defaultValue: "No hay movimientos en este periodo." })}
        </p>
      ) : null}
      {viewMode === "accounts"
        ? accountTotals.map((account) => (
            <article key={account.name} className="flex items-center justify-between gap-4 border-b border-[color:color-mix(in_srgb,var(--app-border)_64%,transparent)] py-4 last:border-b-0">
              <div className="min-w-0">
                <p className="truncate font-medium text-[var(--app-text)]">{account.name}</p>
                <p className="mt-1 text-xs text-[var(--app-text-secondary)]">
                  {t("categories.transactionsCount", { count: account.count, defaultValue: "{{count}} movimientos" })}
                </p>
              </div>
              <p className={`shrink-0 font-semibold tabular-nums ${account.total < 0 ? "text-[var(--app-danger)]" : account.total > 0 ? "text-[var(--app-success)]" : "text-[var(--app-text)]"}`}>{formatSignedCurrency(account.total)}</p>
            </article>
          ))
        : transactions.map((transaction) => {
            const amount = Math.abs(Number(transaction.amount ?? 0));
            const transactionType = transactionTypeById.get(transaction.type_id);
            const signedAmount = transactionType === "EXPENSE"
              ? -amount
              : transactionType === "DEPOSIT"
                ? amount
                : Number(transaction.amount ?? 0);

            return (
              <article key={transaction.id} className="flex items-center justify-between gap-4 border-b border-[color:color-mix(in_srgb,var(--app-border)_64%,transparent)] py-4 last:border-b-0">
                <div className="min-w-0">
                  <p className="truncate font-medium text-[var(--app-text)]">{transaction.name}</p>
                  <p className="mt-1 text-xs text-[var(--app-text-secondary)]">
                    {formatDate(transaction.transaction_date)}
                    {transaction.account ? <span> · {transaction.account}</span> : null}
                  </p>
                </div>
                <p className={`shrink-0 font-semibold tabular-nums ${signedAmount < 0 ? "text-[var(--app-danger)]" : signedAmount > 0 ? "text-[var(--app-success)]" : "text-[var(--app-text)]"}`}>{formatSignedCurrency(signedAmount)}</p>
              </article>
            );
          })}
    </ModalShell>
  );
};
